import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster, toast as sonnerToast } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BrandProvider } from "./context/BrandContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import BrandKB from "./pages/BrandKB";
import BatchGenerator from "./pages/BatchGenerator";
import ReviewFeed from "./pages/ReviewFeed";
import CalendarPage from "./pages/CalendarPage";
import Inbox from "./pages/Inbox";
import Competitors from "./pages/Competitors";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import DenzelShareTray from "./pages/DenzelShareTray";
import WeeklyRecap from "./pages/WeeklyRecap";
import DigestDetail from "./pages/DigestDetail";
import ApprovalPortal from "./pages/ApprovalPortal";

// ---------------------------------------------------------------------------
// Persistent error toasts — override sonner's `toast.error` at import time so
// every existing `toast.error(...)` call across the app stays on screen until
// the user taps the OK / close button. Fixes the "the PDF upload error
// flashed too quickly for me to read" bug report. The OK button also copies
// the error message to the clipboard so long Meta / X / LinkedIn rejection
// strings can be pasted into a bug report in one tap. Callers can still
// pass their own `title` / `description`, but they can't accidentally
// shorten the duration or strip the OK action (defaults win the spread).
// Non-error toast variants (success / info / warning / loading / promise)
// are untouched — errors are the only ones we force to be dismissed by hand.
// ---------------------------------------------------------------------------
if (!sonnerToast.__persistentErrorPatched) {
  const _origError = sonnerToast.error.bind(sonnerToast);
  sonnerToast.error = (message, options = {}) =>
    _origError(message, {
      ...options,
      duration: Infinity,
      closeButton: true,
      cancel: {
        label: "OK · copy",
        onClick: () => {
          // Best-effort clipboard copy. Some browsers (Safari, older FF, non-HTTPS)
          // block the modern API — fall back silently if it errors.
          try {
            const text = typeof message === "string"
              ? message
              : (options?.description || options?.title || "").toString();
            if (text && navigator?.clipboard?.writeText) {
              navigator.clipboard.writeText(text).then(
                () => sonnerToast.success("Error copied to clipboard",
                                          { duration: 2000 }),
                () => {},
              );
            }
          } catch (_) { /* clipboard unavailable — dismiss silently */ }
        },
      },
    });
  sonnerToast.__persistentErrorPatched = true;
}

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><img src="/logo.png" alt="" className="w-16 h-16 animate-pulse rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <BrandProvider><Layout>{children}</Layout></BrandProvider>;
};

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/approve/:token" element={<ApprovalPortal />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/brand" element={<Protected><BrandKB /></Protected>} />
      <Route path="/generator" element={<Protected><BatchGenerator /></Protected>} />
      <Route path="/review" element={<Protected><ReviewFeed /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/inbox" element={<Protected><Inbox /></Protected>} />
      <Route path="/competitors" element={<Protected><Competitors /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/denzel-share" element={<Protected><DenzelShareTray /></Protected>} />
      <Route path="/recap" element={<Protected><WeeklyRecap /></Protected>} />
      <Route path="/recap/:brandName" element={<Protected><WeeklyRecap /></Protected>} />
      <Route path="/digest" element={<Protected><DigestDetail /></Protected>} />
      <Route path="/digest/:digestId" element={<Protected><DigestDetail /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster
        richColors
        position="top-right"
        closeButton
        toastOptions={{
          // Error toasts stay on screen until the user taps the OK / close
          // button — "the error flashed too quickly to read" bug fix + the
          // Feb-2026 "make the OK dismiss button clearly tappable" ask.
          classNames: {
            error: "sonner-error-persistent",
            cancelButton:
              "!bg-primary !text-primary-foreground !font-semibold " +
              "!px-3 !py-1.5 !rounded-md !text-xs !ml-2 " +
              "hover:!bg-primary/90 !cursor-pointer",
            closeButton:
              "!bg-background !border-border !text-foreground " +
              "hover:!bg-muted !cursor-pointer",
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
